import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { api } from '../apiConfig';
import ExhibitionPhotoCarousel from '../components/ExhibitionPhotoCarousel';
import managerStyles from '../components/ExhibitionManager.module.css';
import { copyText } from '../utils/clipboard';
import { exhibitionPublicUrl } from '../utils/exhibitionLinks';
import { formatBuyerDisplayPrice } from '../utils/pricing';
import styles from './ExhibitionPage.module.css';

const parseDate = (value) => {
  if (!value) return null;
  const [year, month, day] = String(value).slice(0, 10).split('-').map(Number);
  return new Date(year, month - 1, day);
};

const formatDate = (value, options = { month: 'long', day: 'numeric', year: 'numeric' }) => {
  const date = parseDate(value);
  return date ? date.toLocaleDateString(undefined, options) : '';
};

const exhibitionTiming = (start, end) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const startDate = parseDate(start);
  const endDate = parseDate(end);
  if (startDate && today < startDate) {
    return { label: `Opening ${formatDate(start, { month: 'short', day: 'numeric' })}`, tone: 'upcoming' };
  }
  if (endDate && today > endDate) return { label: 'Closed', tone: 'closed' };
  return { label: endDate ? `On view through ${formatDate(end, { month: 'short', day: 'numeric' })}` : 'On view', tone: 'current' };
};

const websiteHref = (website) => (/^https?:\/\//i.test(website) ? website : `https://${website}`);

const humanize = (value) => String(value || '')
  .replace(/[_-]+/g, ' ')
  .replace(/^\w/, (letter) => letter.toUpperCase());

const formatDimensions = (dimensions) => {
  if (!dimensions || typeof dimensions !== 'object') return typeof dimensions === 'string' ? dimensions : '';
  const sizes = [dimensions.height, dimensions.width, dimensions.depth]
    .map(Number)
    .filter((size) => Number.isFinite(size) && size > 0);
  if (sizes.length === 0) return '';
  const unit = /^c/i.test(dimensions.unit || dimensions.units || '') ? 'cm' : 'in';
  return `${sizes.join(' × ')} ${unit}`;
};

const artistWorksUrl = (name) => `https://artemerge.org/collections/vendors?q=${encodeURIComponent(name)}`;

const initial = (name) => String(name || '?').trim().charAt(0).toUpperCase();

const ORGANIZER_HEADINGS = {
  gallery: 'About the gallery',
  curator: 'About the curator',
  artist: 'About the artist'
};

const ExhibitionPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [exhibition, setExhibition] = useState(null);
  const [status, setStatus] = useState('loading');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    api.get(`/explore/exhibitions/${encodeURIComponent(slug)}`)
      .then((response) => {
        if (cancelled) return;
        setExhibition(response.data);
        setStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setStatus('missing');
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    if (exhibition?.slug && exhibition.slug !== slug) {
      navigate(`/exhibitions/${exhibition.slug}`, { replace: true });
    }
  }, [exhibition, slug, navigate]);

  useEffect(() => {
    if (!exhibition) return undefined;
    const previous = document.title;
    document.title = `${exhibition.title} | Art Emerge`;
    return () => {
      document.title = previous;
    };
  }, [exhibition]);

  if (status === 'loading') {
    return <div className={managerStyles.loading}>Loading exhibition...</div>;
  }

  if (status === 'missing') {
    return (
      <div className={styles.page}>
        <div className={managerStyles.emptyState}>
          <h3>This exhibition is not available</h3>
          <p>It may not be published yet, or the link may be incorrect.</p>
          <Link to="/marketplace" className={managerStyles.editButton}>Browse the marketplace</Link>
        </div>
      </div>
    );
  }

  const photos = exhibition.photos || [];
  const artworks = exhibition.artworks || [];
  const artists = exhibition.artists || [];
  const organizer = exhibition.organizer;
  const organizerHeading = organizer && ORGANIZER_HEADINGS[organizer.role];
  const showOrganizer = Boolean(organizerHeading)
    && !artists.some((artist) => artist.id === organizer.id)
    && (organizer.about?.length > 0 || organizer.hours?.length > 0 || Boolean(organizer.website));
  const timing = exhibitionTiming(exhibition.start_date, exhibition.end_date);
  const shareUrl = exhibitionPublicUrl(exhibition);
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const copyLink = async () => {
    try {
      await copyText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Link copied');
    } catch (err) {
      toast.error(`Could not copy the link. Copy it from here: ${shareUrl}`, { autoClose: 10000 });
    }
  };

  const share = async () => {
    try {
      await navigator.share({ title: exhibition.title, url: shareUrl });
    } catch (err) {
      if (err?.name !== 'AbortError') copyLink();
    }
  };

  return (
    <article className={styles.page}>
      {photos.length > 0 ? (
        <div className={managerStyles.previewCarousel}>
          <ExhibitionPhotoCarousel photos={photos} title={exhibition.title} />
        </div>
      ) : exhibition.cover_image_url && (
        <img src={exhibition.cover_image_url} alt={exhibition.title} className={managerStyles.previewHero} />
      )}

      <div className={styles.intro}>
        <header className={styles.introMain}>
          <div className={styles.eyebrow}>
            <span>Exhibition</span>
            <span className={`${styles.timing} ${styles[`timing_${timing.tone}`]}`}>{timing.label}</span>
          </div>
          <h1 className={styles.title}>{exhibition.title}</h1>
          {exhibition.description && <p className={styles.description}>{exhibition.description}</p>}
        </header>

        <aside className={styles.visitCard} aria-label="Exhibition details">
          <dl className={styles.facts}>
            <div>
              <dt>Dates</dt>
              <dd>{formatDate(exhibition.start_date)} – {formatDate(exhibition.end_date)}</dd>
            </div>
            {exhibition.location && (
              <div>
                <dt>Location</dt>
                <dd>{exhibition.location}</dd>
              </div>
            )}
            {exhibition.organizer_name && (
              <div>
                <dt>Presented by</dt>
                <dd className={styles.organizer}>
                  {exhibition.organizer_avatar
                    ? <img src={exhibition.organizer_avatar} alt="" className={styles.avatarSmall} />
                    : <span className={styles.avatarSmall} aria-hidden="true">{initial(exhibition.organizer_name)}</span>}
                  <span>
                    <strong>{exhibition.organizer_name}</strong>
                    {exhibition.organizer_website && (
                      <a href={websiteHref(exhibition.organizer_website)} target="_blank" rel="noopener noreferrer">
                        {exhibition.organizer_role === 'gallery' ? 'Gallery website' : 'Website'}
                      </a>
                    )}
                  </span>
                </dd>
              </div>
            )}
            {artworks.length > 0 && (
              <div>
                <dt>On view</dt>
                <dd>
                  {artworks.length} {artworks.length === 1 ? 'artwork' : 'artworks'}
                  {artists.length > 0 ? ` by ${artists.length} ${artists.length === 1 ? 'artist' : 'artists'}` : ''}
                </dd>
              </div>
            )}
          </dl>
          <div className={styles.shareActions}>
            {canShare && (
              <button type="button" className={managerStyles.editButton} onClick={share}>Share</button>
            )}
            <button type="button" className={managerStyles.editButton} onClick={copyLink}>
              {copied ? 'Link copied' : 'Copy link'}
            </button>
          </div>
        </aside>
      </div>

      {artworks.length > 0 && (
        <section className={styles.section} aria-labelledby="exhibition-artworks">
          <h2 id="exhibition-artworks" className={styles.sectionTitle}>Artworks</h2>
          <div className={styles.artworkGrid}>
            {artworks.map((artwork) => {
              const sold = artwork.is_sold || artwork.status === 'sold';
              const productUrl = artwork.shopify_handle && !sold
                ? `https://artemerge.org/products/${artwork.shopify_handle}`
                : null;
              const details = [
                humanize(artwork.medium),
                formatDimensions(artwork.dimensions),
                artwork.year_created
              ].filter(Boolean).join(' · ');
              const price = sold ? '' : formatBuyerDisplayPrice(artwork.price, { estimatedShipping: artwork.estimated_shipping_price });
              const frame = (
                <span className={styles.artworkFrame}>
                  {artwork.image_url
                    ? <img src={artwork.image_url} alt={artwork.title} loading="lazy" />
                    : <span className={managerStyles.previewPlaceholder} />}
                </span>
              );
              return (
                <figure key={artwork.id} className={styles.artworkCard}>
                  {productUrl ? <a href={productUrl} className={styles.artworkImageLink}>{frame}</a> : frame}
                  <figcaption className={styles.artworkCaption}>
                    <h3>{productUrl ? <a href={productUrl}>{artwork.title}</a> : artwork.title}</h3>
                    {artwork.artist_name && <p className={styles.artworkArtist}>{artwork.artist_name}</p>}
                    {details && <p className={styles.artworkDetails}>{details}</p>}
                    <div className={styles.artworkFooter}>
                      {sold
                        ? <span className={managerStyles.statusChip}>Sold</span>
                        : price && <span className={styles.artworkPrice}>{price}</span>}
                      {productUrl && <a href={productUrl} className={styles.artworkCta}>View artwork</a>}
                    </div>
                  </figcaption>
                </figure>
              );
            })}
          </div>
        </section>
      )}

      {artists.length > 0 && (
        <section className={styles.section} aria-labelledby="exhibition-artists">
          <h2 id="exhibition-artists" className={styles.sectionTitle}>
            {artists.length === 1 ? 'The artist' : 'Artists in this exhibition'}
          </h2>
          <div className={styles.artistGrid}>
            {artists.map((artist) => (
              <div key={artist.id} className={styles.artistCard}>
                {artist.avatar
                  ? <img src={artist.avatar} alt="" className={styles.avatarLarge} />
                  : <span className={styles.avatarLarge} aria-hidden="true">{initial(artist.name)}</span>}
                <div className={styles.artistBody}>
                  <h3>{artist.name}</h3>
                  {artist.location && <p className={styles.artistLocation}>{artist.location}</p>}
                  {artist.summary && <p className={styles.artistSummary}>{artist.summary}</p>}
                  <a href={artistWorksUrl(artist.name)} className={managerStyles.textButton}>
                    View available works
                  </a>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {showOrganizer && (
        <section className={styles.section} aria-labelledby="exhibition-organizer">
          <h2 id="exhibition-organizer" className={styles.sectionTitle}>{organizerHeading}</h2>
          <div className={styles.aboutCard}>
            <div className={styles.aboutIdentity}>
              {organizer.avatar
                ? <img src={organizer.avatar} alt="" className={styles.avatarLarge} />
                : <span className={styles.avatarLarge} aria-hidden="true">{initial(organizer.name)}</span>}
              <div>
                <h3>{organizer.name}</h3>
                {organizer.location && <p className={styles.artistLocation}>{organizer.location}</p>}
              </div>
            </div>
            {organizer.about?.length > 0 && (
              <div className={styles.aboutText}>
                {organizer.about.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
              </div>
            )}
            {(organizer.hours?.length > 0 || organizer.website || organizer.instagram || organizer.available_count > 0) && (
              <div className={styles.aboutMeta}>
                {organizer.hours?.length > 0 && (
                  <div>
                    <h4>Hours</h4>
                    {organizer.hours.map((line) => <p key={line}>{line}</p>)}
                  </div>
                )}
                <div className={styles.aboutLinks}>
                  {organizer.website && (
                    <a href={websiteHref(organizer.website)} target="_blank" rel="noopener noreferrer" className={managerStyles.textButton}>
                      Visit website
                    </a>
                  )}
                  {organizer.instagram && (
                    <a href={organizer.instagram} target="_blank" rel="noopener noreferrer" className={managerStyles.textButton}>
                      Instagram
                    </a>
                  )}
                  {organizer.available_count > 0 && (
                    <a href={artistWorksUrl(organizer.name)} className={managerStyles.textButton}>
                      View available works
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>
      )}
    </article>
  );
};

export default ExhibitionPage;
